import type { ProductResult } from '@/lib/services/products/products.types'
import type { AdminRouterContext } from '@/lib/router-context'
import { RecordHistoryPage } from '@/components/audit-log/RecordHistoryPage'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminTitleHead } from '@/lib/admin-route-title'
import { auditLogQueries } from '@/lib/services/audit-log/audit-log.query'
import { productFilters, productQueries } from '@/lib/services/products/products.query'
import { productParamsSchema } from '@/lib/services/products/products.schema'
import { createFileRoute, notFound } from '@tanstack/react-router'

const filters = productFilters().get()

export const Route = createFileRoute('/_authenticated/$orgId/products/$productId_/history')({
  component: ProductHistoryPage,
  params: {
    parse: (params) => productParamsSchema.parse(params),
  },
  head: ({ loaderData, params }) =>
    adminTitleHead(
      'History',
      loaderData?.product?.name || `Product #${params?.productId}`,
      'Products'
    ),
  loader: loadProductHistory,
})

export async function loadProductHistory({
  context,
  params,
}: {
  context: AdminRouterContext
  params: { orgId: number | string; productId: number | string }
}): Promise<{ product: ProductResult }> {
  if (!context.ability.can('view', 'audit-logs') || !context.ability.can('view', 'products')) {
    throw notFound()
  }

  const [product] = await Promise.all([
    context.queryClient.ensureQueryData(
      productQueries.show({
        organizationId: params.orgId,
        productId: params.productId,
        filters,
      })
    ),
    context.queryClient.ensureInfiniteQueryData(
      auditLogQueries.record({
        organizationId: params.orgId,
        subject: 'products',
        recordId: params.productId,
      })
    ),
  ])

  return { product }
}

export function ProductHistoryPage() {
  const { orgId, productId } = Route.useParams()
  const { product } = Route.useLoaderData()
  const path = usePathHelper()

  return (
    <RecordHistoryPage
      backLabel="product"
      backLink={path.products.show(productId).link}
      description="Review important changes to this product's details, pricing, variants, memberships, and inventory."
      organizationId={product.organization_id ?? orgId}
      recordId={productId}
      recordLabel={product.name || `Product #${productId}`}
      subject="products"
    />
  )
}
